import { useEffect, useState, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useMutation } from '@apollo/client';
import { motion, AnimatePresence } from 'framer-motion';
import { UserX } from 'lucide-react';

import { BLOCK_USER } from '../../services/apollo/mutations/blockMutations';
import { addBlockedUser } from '../../slices/blocksSlice';

const BlockUserConfirmation = ({
  isOpen,
  onClose,
  username = '',
  userId,
  onBlocked,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const [blockUser] = useMutation(BLOCK_USER);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !submitting) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, submitting, onClose]);

  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;

      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.overflow = '';

        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  async function handleConfirm() {
    if (!userId || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await blockUser({ variables: { userId } });
      const u = data?.blockUser;
      if (u) {
        dispatch(addBlockedUser(u));
        onBlocked?.(u);
      }
      onClose();
    } catch (err) {
      setError(
        err?.message ||
          t(
            'block_user_error',
            'Could not block user. Please try again.'
          )
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm touch-none"
            onClick={!submitting ? onClose : undefined}
            onWheel={(e) => e.preventDefault()}
            onTouchMove={(e) => e.preventDefault()}
          />

          <div
            className="fixed inset-0 z-[111] flex items-center justify-center px-6"
            onClick={!submitting ? onClose : undefined}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="bg-white w-full max-w-sm rounded-2xl overflow-hidden shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="block-modal-title"
              aria-describedby="block-modal-description"
            >
              <div className="p-6 text-center space-y-3">
                <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                  <UserX className="w-7 h-7 text-red-500" />
                </div>

                <h3
                  id="block-modal-title"
                  className="text-lg font-bold text-foreground"
                >
                  {t('block_user_title', 'Block @{{username}}?', { username })}
                </h3>

                <p
                  id="block-modal-description"
                  className="text-sm text-muted-foreground leading-relaxed"
                >
                  {t(
                    'block_user_description',
                    "They won't be able to message you, and their posts will be hidden from your feed. You can unblock them anytime from your settings."
                  )}
                </p>

                {error && (
                  <p
                    role="alert"
                    className="text-sm text-red-600 leading-relaxed"
                  >
                    {error}
                  </p>
                )}
              </div>

              <div className="flex border-t border-border">
                <button
                  onClick={onClose}
                  disabled={submitting}
                  className="flex-1 py-4 text-sm font-semibold text-foreground 
                             hover:bg-gray-50 transition-colors border-r border-border
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={submitting || !userId}
                  className="flex-1 py-4 text-sm font-semibold text-red-600 
                             hover:bg-red-50 transition-colors
                             disabled:opacity-50 disabled:cursor-not-allowed
                             flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-red-600/30 border-t-red-600 rounded-full animate-spin" />
                      {t('blocking', 'Blocking...')}
                    </>
                  ) : (
                    t('block', 'Block')
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default memo(BlockUserConfirmation);
